import { useCallback, useRef, useState, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useMutation } from '@apollo/client';
import cn from 'classnames';
import dynamic from 'next/dynamic';
import { isSafari } from 'react-device-detect';
// Api
import { TRACK_SHARE } from 'api/store/mutations';
// Types
import { GetMediaPost_getMediaPost_VideoPost } from 'api/mediaPost/types/GetMediaPost';
import { TrackShare, TrackShareVariables } from 'api/store/types/TrackShare';
import {
  TrackedShareTarget,
  TrackedSourceType,
} from 'api/graphql-global-types';
// Helpers
import { getEnvLink } from 'helpers/routes';
import { parseMediaStartTimeSec } from 'helpers/mediaStartTime';
import { isStoreOwner } from 'helpers/user';
// Hooks
import { useBreakpoints, useGetCurrUser } from 'hooks';
import {
  useTrackViews,
  useWatchTimeTracker,
  AnalyticsTargetType,
} from 'hooks/analytics';
// Constants
import { VIDEO_SOCIAL_MEDIA_POST } from 'constants/routes';
import { BLACK_MODAL_BACKGROUND_STYLE } from 'constants/colors';
// Components
import { showToast } from 'components/common/Toast/Toast';
import VideoHeader from 'components/common3/VideoHeader/VideoHeader';
import VideoDetails from 'components/common3/VideoDetails/VideoDetails';
// Helpers
import { getVideoPostDetails } from 'helpers/mediaPost';
// Ui
import VideoPlayer from 'ui/VideoPlayer/VideoPlayer';
// Styles
import styles from './MediaFeedItemVideo.module.scss';
// Dynamic imports
const MediaFeedItemButtons = dynamic(
  import('../MediaFeedItemButtons/MediaFeedItemButtons'),
  {
    ssr: false,
  }
);
const UploadMediaChannel = dynamic(
  import(
    '../../../../ProfileSetup/components/ChannelCreation/ChannelCard/UploadMediaChannel/UploadMediaChannel'
  ),
  {
    ssr: false,
  }
);

type MediaFeedItemVideoProps = {
  mediaFeed: GetMediaPost_getMediaPost_VideoPost;
};

const MediaFeedItemVideo = ({
  mediaFeed,
}: MediaFeedItemVideoProps): JSX.Element | null => {
  // State
  const [
    mediaPostModalVisibility,
    setMediaPostModalVisibility,
  ] = useState<boolean>(false);
  const [viewRecorded, setViewRecorded] = useState(false);

  // Custom hooks
  const router = useRouter();
  const startTimeSec = parseMediaStartTimeSec(router.query);
  const { isBreakpointL } = useBreakpoints();

  // Refs
  const rootRef = useRef<HTMLDivElement | null>(null);
  const watchTimeIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );

  // Mutations
  const [trackShare] = useMutation<TrackShare, TrackShareVariables>(
    TRACK_SHARE
  );

  // Custom hooks
  const { data } = useGetCurrUser();

  // Analytics hooks - Views Algorithm (V_A direct view, V_D watch-time bonus)
  const { trackDirectView, trackMessageShare } = useTrackViews({
    targetType: AnalyticsTargetType.MEDIA_POST,
  });
  const { startWatching, stopWatching, checkWatchTime } = useWatchTimeTracker(
    mediaFeed.id
  );
  const checkWatchTimeRef = useRef(checkWatchTime);
  checkWatchTimeRef.current = checkWatchTime;

  // Clean up watch time interval on unmount
  useEffect(() => {
    return () => {
      if (watchTimeIntervalRef.current) {
        clearInterval(watchTimeIntervalRef.current);
        watchTimeIntervalRef.current = null;
      }
    };
  }, []);

  // Callbacks
  const handleMediaPostViewed = useCallback(
    async (postId: string) => {
      try {
        await trackDirectView(postId);
        setViewRecorded(true);
      } catch (error) {
        console.error(error);
      }
    },
    [trackDirectView]
  );

  // Constants
  const { store, title, slug } = mediaFeed;
  const isOwner = isStoreOwner(data, store.id);
  const storeSlug = store.slug;
  const videoDetails = getVideoPostDetails(mediaFeed);
  const link = `${getEnvLink()}/${storeSlug}${VIDEO_SOCIAL_MEDIA_POST}${slug}`;

  // Event handlers
  const handleMediaPostModalOpen = () => {
    setMediaPostModalVisibility(true);
  };

  const handleMediaPostModalClose = () => {
    setMediaPostModalVisibility(false);
  };

  const handleVideoPlayerOnPlay = () => {
    if (!viewRecorded) {
      handleMediaPostViewed(mediaFeed.id);
    }

    // Start watch time tracking
    startWatching();

    // Set up interval to check watch time every second (V_D bonus after 10s)
    if (!watchTimeIntervalRef.current) {
      watchTimeIntervalRef.current = setInterval(() => {
        checkWatchTimeRef.current();
      }, 1000);
    }
  };

  const handleVideoPlayerOnPause = () => {
    // Stop the watch time check interval when paused
    if (watchTimeIntervalRef.current) {
      clearInterval(watchTimeIntervalRef.current);
      watchTimeIntervalRef.current = null;
    }
    stopWatching();
  };

  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(link);

      await trackMessageShare(mediaFeed.id);

      // Also track share for backwards compatibility
      await trackShare({
        variables: {
          input: {
            shareTarget: TrackedShareTarget.Copy,
            sourceId: mediaFeed.id,
            sourceType: TrackedSourceType.MediaPost,
          },
        },
      });

      showToast({
        message: 'Copied',
        autoClose: 1000,
      });
    } catch (error) {
      showToast({
        message: (error as Error).message || 'Could not copy text',
        type: 'error',
        autoClose: 1000,
      });
    }
  };

  return (
    <div className={styles.root} ref={rootRef}>
      <div className={styles.contentWrapper}>
        <div
          className={cn(styles.videoContainer, {
            [styles.videoSafariContainer]: isSafari,
            [styles.videoIsNotSafariContainer]: !isSafari,
          })}
        >
          <VideoPlayer
            videoClassName={styles.videoPlayer}
            onPlay={handleVideoPlayerOnPlay}
            onPause={handleVideoPlayerOnPause}
            autoPlay={!!mediaFeed.video?.url}
            src={mediaFeed.video.url || ''}
            volumeControl="speaker"
            poster={
              mediaFeed.thumbnailUrl || mediaFeed.video.thumbnailUrl || ''
            }
            // Real <img> poster in SSR HTML — <video poster> alone often
            // paints as an empty dark box in HTML previews / non-JS clients.
            videoPoster={
              mediaFeed.thumbnailUrl || mediaFeed.video.thumbnailUrl || ''
            }
            initialDurationSec={
              mediaFeed.video.duration
                ? mediaFeed.video.duration / 1000
                : undefined
            }
            initialTimeSec={startTimeSec}
          />
        </div>
        <div className={styles.bodyTextWrapper}>
          <VideoHeader
            title={title}
            store={store}
            isOwner={isOwner}
            actionButtons={
              <MediaFeedItemButtons
                store={store}
                handleMediaPostModalOpen={handleMediaPostModalOpen}
                handleShare={handleShare}
                isOwner={isOwner}
                mediaFeed={mediaFeed}
                className={styles.mediaFeedItemButtons}
              />
            }
          />

          <VideoDetails videoDetails={videoDetails} />
        </div>
      </div>
      {isOwner && (
        <UploadMediaChannel
          mediaFeed={mediaFeed}
          oldSlug={slug}
          modalProps={{
            open: mediaPostModalVisibility,
            onClose: handleMediaPostModalClose,
            title: 'Edit Media Post',
            fullPageOnMobile: true,
            blockScroll: isBreakpointL,
            modalStyles: BLACK_MODAL_BACKGROUND_STYLE,
          }}
        />
      )}
    </div>
  );
};
export default MediaFeedItemVideo;
